import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import withLayout from "../layout";
import RentHeader, { RentFooter } from "../rent/RentHeader";
import { cancelBooking, getBookings, requestAccessLink } from "../rent/api";
import { euro, fmtDate, fmtDateTime, isNightType, normalizeTariff } from "../rent/tariffs";
import { Icons, tariffName } from "../rent/ui";
import { buildPlate } from "../booking";
import "../login/login.css";

/*
 * Meine Buchungen – ohne Konto:
 * 1. Kennzeichen + E-Mail -> Backend schickt einen Link mit Token (/meine-buchungen?token=…)
 * 2. Mit Token: Buchungen anzeigen, Abos kündigen
 * Erwartetes Buchungsformat: { id, status: active|upcoming|ended|cancelled, tariffType, tariffName?, title, start, end?, total, monthly?, nextCharge?, receiptUrl? }
 */
const MyBookings = ({ setLoaderBox }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [query] = useSearchParams();
  const token = query.get("token");
  const [plate, setPlate] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [list, setList] = useState(null);

  useEffect(() => {
    document.title = "Meine Buchungen | innopark24";
  }, []);

  const load = () => {
    setLoaderBox(true);
    getBookings(token)
      .then((data) => setList(data))
      .catch(() => setError(t("rxMyLoadError")))
      .finally(() => setLoaderBox(false));
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onRequest = async (e) => {
    e.preventDefault();
    setError("");
    const licensePlate = buildPlate(plate, "de") || buildPlate(plate, "int");
    const mailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    setErrors({ plate: !licensePlate, email: !mailOk });
    if (!licensePlate || !mailOk) return;
    setLoaderBox(true);
    try {
      await requestAccessLink({ licensePlate, email: email.trim(), lang, returnUrl: `${window.location.origin}/meine-buchungen` });
      setSent(true);
    } catch (err) {
      setError(t("rxMyLinkError"));
    }
    setLoaderBox(false);
  };

  const onCancel = async (b) => {
    if (!window.confirm(t("rxCancelConfirm"))) return;
    setLoaderBox(true);
    try {
      await cancelBooking(b.id, token);
      load();
    } catch (err) {
      setError(t("rxCancelError"));
      setLoaderBox(false);
    }
  };

  const active = (list || []).filter((b) => b.status === "active" || b.status === "upcoming");
  const past = (list || []).filter((b) => b.status !== "active" && b.status !== "upcoming");

  const item = (b) => {
    const tariff = normalizeTariff({ type: b.tariffType, name: b.tariffName, price: b.total }) || { type: "hour", name: b.tariffName };
    const statusChip = {
      active: ["green", t("rxActive")],
      upcoming: ["blue", t("rxUpcoming")],
      cancelled: ["", t("rxCancelled")],
      ended: ["", t("rxEnded")],
    }[b.status] || ["", b.status];
    return (
      <div className="rx-bk" key={b.id}>
        <div className={`rx-ic ${isNightType(tariff) ? "night" : ""}`}>{Icons[tariff.type] || Icons.hour}</div>
        <div>
          <h3>{`${tariffName(tariff, t)} · ${b.title}`}</h3>
          <p>
            {b.monthly
              ? t("rxNextCharge", { date: b.nextCharge ? fmtDate(b.nextCharge, lang, { year: "numeric" }) : "–", amount: euro(b.total, lang) })
              : `${fmtDateTime(b.start, lang)}${b.end ? ` – ${fmtDateTime(b.end, lang)}` : ""} · ${euro(b.total, lang)}`}
          </p>
        </div>
        <div className="rx-r">
          <span className={`rx-chip ${statusChip[0]}`}>{statusChip[1]}</span>
          {b.monthly && b.status === "active" && (
            <button type="button" className="rx-btn-ghost small" onClick={() => onCancel(b)}>
              {t("rxCancel")}
            </button>
          )}
          {b.receiptUrl && (
            <a className="rx-btn-ghost small" href={b.receiptUrl} target="_blank" rel="noopener noreferrer">
              {t("rxReceipt")}
            </a>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="rp">
      <RentHeader />
      <main className="rp-main">
        <div className="rx-my">
          <section className="rx-card rx-panel">
            <h1 style={{ fontSize: 26, marginBottom: 6 }}>{t("rxNavMy")}</h1>
            <p className="rx-muted" style={{ fontSize: 16, marginBottom: 16 }}>
              {t("rxMyLead")}
            </p>
            <form onSubmit={onRequest} noValidate>
              <div className={`ipl-field ${errors.plate ? "has-error" : ""}`}>
                <label htmlFor="my-plate">{t("licensePlate")}</label>
                <div className="ipl-input">
                  {Icons.car}
                  <input id="my-plate" value={plate} autoCapitalize="characters" onChange={(e) => setPlate(e.target.value.toUpperCase())} placeholder="M-AB1234" />
                </div>
                <div className="ipl-err">{t("rpPlateErrorDe")}</div>
              </div>
              <div className={`ipl-field ${errors.email ? "has-error" : ""}`}>
                <label htmlFor="my-mail">{t("rpEmail")}</label>
                <div className="ipl-input">
                  {Icons.mail}
                  <input id="my-mail" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@beispiel.de" />
                </div>
                <div className="ipl-err">{t("rpEmailError")}</div>
              </div>
              <button className="ipl-btn" type="submit">
                <span>{t("rxSendLink")}</span>
              </button>
            </form>
          </section>

          <div>
            {error && (
              <div className="ipl-status" role="alert" style={{ marginBottom: 14 }}>
                {error}
              </div>
            )}
            {sent && (
              <div className="rx-info-box rx-ok-box">
                {Icons.check}
                <div>
                  <b>{t("rxLinkSentHead")}</b>
                  <p className="rx-muted">{t("rxLinkSentText", { email })}</p>
                </div>
              </div>
            )}
            {!token && !sent && (
              <div className="rx-info-box">
                {Icons.info}
                <div>
                  <b>{t("rxMyHowHead")}</b>
                  <p className="rx-muted">{t("rxMyHowText")}</p>
                </div>
              </div>
            )}
            {list && list.length === 0 && <div className="rx-empty-box">{t("rxMyEmpty")}</div>}
            {active.length > 0 && (
              <>
                <div className="rx-grp" style={{ marginTop: 0 }}>
                  {t("rxMyActive")}
                </div>
                {active.map(item)}
              </>
            )}
            {past.length > 0 && (
              <>
                <div className="rx-grp">{t("rxMyPast")}</div>
                {past.map(item)}
              </>
            )}
          </div>
        </div>
      </main>
      <RentFooter />
    </div>
  );
};

export default withLayout(MyBookings);
